import { ExposureDirectionType, LevelType, SpaceType } from 'data/types';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { openingRunWithOpenNeighborOverhang } from 'utils/open-neighbor-opening-runs';
import { EdgeRunType } from 'utils/opening-position-types';
import { OpeningRunLayoutType } from 'utils/opening-run-layout-types';
import { getPolygonEdgeOpeningRuns } from 'utils/polygon-edge-opening-runs';
import { spaceOpenNeighborRuns } from 'utils/space-opening-run';
import { OpeningRunSpaceType } from 'utils/space-opening-run-types';
import { boundingBoxOfVertices, getSpaceVertices } from 'utils/space-polygon';
import { isDetachedSpaceItem } from 'utils/structure';

const RUN_MATCH_TOLERANCE_FT = 0.1;

type OpeningRunLayoutSpaceType = OpeningRunSpaceType &
  Pick<SpaceType, 'sizingAttributes'>;

export function spaceOpeningRunLayouts(
  space: OpeningRunLayoutSpaceType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined
): OpeningRunLayoutType[] {
  if (!space.position || !space.sizingAttributes) {
    return [];
  }
  const relativeVertices = getSpaceVertices(space);
  if (relativeVertices.length < 3) {
    return [];
  }
  const bb = boundingBoxOfVertices(relativeVertices);
  const originFeet = {
    x: space.position.x + bb.minX,
    y: space.position.y + bb.minY
  };
  const neighbors = spaceOpenNeighborRuns(space, level, homeOrientation);
  return getPolygonEdgeOpeningRuns({
    originFeet,
    verticesFeet: relativeVertices.map((vertex) => ({
      x: vertex.x - bb.minX,
      y: vertex.y - bb.minY
    })),
    footprints: isDetachedSpaceItem(space.itemId)
      ? null
      : getEffectiveFootprintPolygons(level),
    walls: [
      ...space.sizingAttributes.aboveGradeWalls,
      ...space.sizingAttributes.belowGradeWalls
    ],
    homeOrientation
  }).map((layout) => ({
    ...layout,
    run: openingRunWithOpenNeighborOverhang(layout.run, originFeet, neighbors)
  }));
}

export function openingRunsMatch(a: EdgeRunType, b: EdgeRunType): boolean {
  if (a.edge !== b.edge) {
    return false;
  }
  if (a.edgeIndex !== undefined && b.edgeIndex !== undefined) {
    return a.edgeIndex === b.edgeIndex;
  }
  return (
    Math.abs(a.start.x - b.start.x) <= RUN_MATCH_TOLERANCE_FT &&
    Math.abs(a.start.y - b.start.y) <= RUN_MATCH_TOLERANCE_FT &&
    Math.abs(a.lengthFt - b.lengthFt) <= RUN_MATCH_TOLERANCE_FT
  );
}

export function openingRunForEdgeIndex(
  runs: EdgeRunType[],
  edgeIndex: number | null | undefined
): EdgeRunType | undefined {
  if (edgeIndex === null || edgeIndex === undefined) {
    return undefined;
  }
  return runs.find((run) => run.edgeIndex === edgeIndex);
}

export function findOpeningRunLayout(
  space: OpeningRunLayoutSpaceType,
  level: LevelType | null | undefined,
  homeOrientation: ExposureDirectionType | undefined,
  run: EdgeRunType | undefined
): OpeningRunLayoutType | null {
  if (!run) {
    return null;
  }
  return (
    spaceOpeningRunLayouts(space, level, homeOrientation).find((layout) =>
      openingRunsMatch(layout.run, run)
    ) ?? null
  );
}
